import Paragraph from "@/components/common/Typography/Paragraph"
import Text from "@/components/common/Typography/Text"
import Title from "@/components/common/Typography/Title"
import Room from "@/components/hotel/hotel-detail/Rooms/Room"
import HotelName from "@/components/hotel/hotels-result/Results/Content/HotelName"
import MarkIcon from "@/components/icons/MarkIcon"
import StarIcon from "@/components/icons/StartIcon"
import { useAppSelector } from "@/lib/redux/hooks"
import { HotelDetail } from "@/lib/redux/features/hotel/type"
import { parseJson } from "@/utils/safeParse/parseJson"
import { Button, Card, Col, Divider, Image, Modal, Row, Tag } from "antd"
import { useMemo, useRef } from "react"
import ModalCancelBookingHotel, { ModalCancelBookingHotelRef } from "./ModalCancelBookingHotel"
import dayjs from "dayjs"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { useTranslation } from "react-i18next"

const HotelBooking = () => {
  const { getTranslatedText } = useTETranslation()
  const refModalCancelBookingHotel = useRef<ModalCancelBookingHotelRef>(null)
  const [modal, modalCtx] = Modal.useModal()
  const order = useAppSelector(state => state.booking.order_detail.data)
  const { t } = useTranslation()

  const metaData = useMemo<any>(() => {
    return parseJson(order?.metaData) || {}
  }, [order?.metaData])

  const itinData = useMemo<any>(() => {
    return parseJson(order?.itinData) || []
  }, [order?.itinData])

  const bookingStatus = order?.bookingStatus || "";

  const contact = metaData?.booking?.customerDetail || metaData?.customerDetail || {}
  const guest = ((metaData?.booking?.bookingDetails?.property?.rooms || [])[0]?.guestDetails || [])[0] || ((metaData?.bookingDetails?.property?.rooms || [])[0]?.guestDetails || [])[0] || {}
  const checkIn = metaData?.booking?.bookingDetails?.checkIn || metaData?.bookingDetails?.checkIn
  const checkOut = metaData?.booking?.bookingDetails?.checkOut || metaData?.bookingDetails?.checkOut

  const isConfirmed = bookingStatus?.toUpperCase() === "ACTIVE";

  const isCanCancel = isConfirmed && (dayjs(checkIn).add(30, 'minute').valueOf() > dayjs().valueOf())

  return (
    <>
      {modalCtx}
      <ModalCancelBookingHotel ref={refModalCancelBookingHotel} />
      <Row>
        <Title level={4} style={{ marginTop: 0 }}>
          {t("booking.contact_info")}
        </Title>
      </Row>
      <Row align='middle' gutter={[15, 15]} wrap={false}>
        <Col flex={"20rem"}>
          <Text>{t("common.fullname")}:</Text>
        </Col>
        <Col>
          {`(${contact?.title === 'Mr.' ? t("common.mr") : t("common.mrs")}) ${contact?.lastName ? `${contact?.lastName} ` : ''}${contact?.firstName || ''}`}
        </Col>
      </Row>
      <Row align='middle' gutter={[15, 15]} wrap={false}>
        <Col flex={"20rem"}>
          <Text>{t("common.phonenumber")}: </Text>
        </Col>
        <Col>
          {`(${contact?.phone?.areaCode || ''}) ${contact?.phone?.number || ''}`}
        </Col>
      </Row>

      <Row align='middle' gutter={[15, 15]} wrap={false}>
        <Col flex={"20rem"}>
          <Text>Email: </Text>
        </Col>
        <Col>
          {contact?.email || ''}
        </Col>
      </Row>

      <Divider style={{ marginBlock: '1.5rem' }} />
      <Row>
        <Title level={4} style={{ marginTop: 0 }}>
          {t("hotel.checkin_guest_details")}
        </Title>
      </Row>
      <Row align='middle' gutter={[15, 15]} wrap={false}>
        <Col flex={"20rem"}>
          <Text>{t("common.fullname")}:</Text>
        </Col>
        <Col>
          {`${guest?.lastName ? `${guest?.lastName} ` : ''}${guest?.firstName || ''}`}
        </Col>
      </Row>
      <Divider style={{ marginBlock: '1.5rem' }} />
      <Row style={{ marginTop: '1.5rem' }}>
        <Title level={4} style={{ marginTop: 0 }}>
          {t("hotel.checkin_time")}
        </Title>
      </Row>
      <Row>
        <Col flex={"20rem"}>
          <Text>
            {t("hotel.checkin_time")}:
          </Text>
        </Col>
        <Col>
          <Tag color='blue' variant='solid'>{dayjs(checkIn).format('DD-MM-YYYY')}</Tag>
        </Col>
      </Row>
      <Row>
        <Col flex={"20rem"}>
          <Text>
            {t("hotel.checkout_time")}:
          </Text>
        </Col>
        <Col>
          <Tag color='blue' variant='solid'>{dayjs(checkOut).format('DD-MM-YYYY')}</Tag>
        </Col>
      </Row>
      <Divider style={{ marginBlock: '1.5rem' }} />
      <Row>
        <Title level={4} style={{ marginTop: 0 }}>
          {t("hotel.property_information")}
        </Title>
      </Row>

      {itinData.map((hotelDetail: HotelDetail) => {
        const pictures = hotelDetail.pictures
        const smallPictures = pictures.slice(1, 5)
        const lastSmallPicture = smallPictures[smallPictures.length - 1]
        const translatedName = hotelDetail.translatedName
        const hotelName = hotelDetail.hotelName
        const address = (hotelDetail?.addresses || []).find(a => a.addressType === 'Local language')
        return (
          <>
            <Row gutter={[10, 10]} style={{ marginTop: '1rem' }}>
              <Image.PreviewGroup
                previewPrefixCls="preview__hotel"
                items={pictures.map((pic: any) => pic.URL.replace('312x', '1024x'))}
              >
                <Col span={12} style={{ height: '100%' }}>
                  <Image
                    className="hotel__image"
                    src={pictures[0]?.URL.replace("s=312x", "s=1024x")}
                    alt={getTranslatedText(pictures[0]?.captionTranslated, pictures[0]?.caption)}
                    style={{ height: '30rem', width: '100%', objectFit: 'cover' }}
                    styles={{
                      root: { height: '100%', width: '100%' }
                    }}
                  />
                </Col>

                <Col span={12}>
                  <Row gutter={[10, 10]}>
                    {smallPictures.slice(0, smallPictures.length - 1).map((img: any) => {
                      return (
                        <Col span={12} key={img.pictureId}>
                          <Image
                            className="hotel__image"
                            src={img.URL.replace("s=312x", "s=500x")}
                            alt={getTranslatedText(img.captionTranslated, img.caption)}
                            style={{ height: 'calc(15rem - 7.5px)', width: '100%', objectFit: 'cover' }}
                            styles={{
                              root: { height: '100%', width: '100%' }
                            }}
                          />
                        </Col>
                      )
                    })}
                    <Col span={12} key={lastSmallPicture?.pictureId}>
                      <Image
                        className="hotel__image"
                        src={lastSmallPicture?.URL}
                        alt={getTranslatedText(lastSmallPicture?.captionTranslated, lastSmallPicture?.caption)}
                        classNames={{
                          root: 'hotel__image--wrapper'
                        }}
                        style={{ height: 'calc(15rem - 7.5px)', width: '100%', objectFit: 'cover' }}
                        styles={{
                          root: { height: '100%', width: '100%' }
                        }}
                      />
                    </Col>
                  </Row>
                </Col>
              </Image.PreviewGroup>
            </Row>
            <HotelName name={getTranslatedText(translatedName, hotelName)} />
            <Row>
              <Text className="home__deals-hotels--item-preview">
                <Text style={{ fontSize: '1.6rem' }}>{hotelDetail.accommodationType}</Text> - <Text className="home__deals-hotels--item-star">
                  <StarIcon style={{ fontSize: '1.6rem' }} /> {hotelDetail.starRating}
                </Text>
              </Text>
            </Row>
            <Row style={{ marginBottom: 5 }} gutter={[10, 10]} align='top' wrap={false}>
              <Col>
                <Text style={{ fontSize: '1.4rem' }}>
                  <MarkIcon style={{ fontSize: '1.6rem' }} />
                </Text>
              </Col>
              <Col flex={1}>
                <Text style={{ fontSize: '1.4rem' }} strong={true}>
                  {`${address?.addressLine_1}, ${[address?.city || '', address?.country || ''].filter(str => !!str).join(', ')}`}
                </Text>
              </Col>
            </Row>
            <Row gutter={[100, 15]} wrap={false} justify='space-between' style={{ marginTop: '0.5rem' }}>
              <Col flex={1}>
                <Paragraph
                  ellipsis={{
                    rows: 4,
                    expandable: true,
                    expanded: false,
                    onExpand(e, info) {
                      modal.info({
                        content: hotelDetail?.descriptions?.overview,
                        width: 800,
                        maskClosable: true,
                        centered: true,
                        title: t("common.introduction"),
                        styles: {
                          body: {
                            maxHeight: 'calc(-80px + 100vh)',
                            overflow: 'auto'
                          }
                        }
                      })
                    }
                  }}
                >
                  {hotelDetail?.descriptions?.overview}
                </Paragraph>
              </Col>
            </Row>

            <Row>
              <Title level={4} style={{ marginTop: 0 }}>
                {t("hotel.room_info_title")}
              </Title>
            </Row>
            <Room
              shownPrice={false}
              options={hotelDetail.searchResponse.rooms as any}
            />
            <Row>
              <Title level={4}>
                {t("hotel.cancellation_policy")}
              </Title>
            </Row>
            <Row>
              <Paragraph>
                {getTranslatedText(
                  (hotelDetail.searchResponse.rooms as any[])[0]?.cancellationPolicy?.translatedCancellationText,
                  (hotelDetail.searchResponse.rooms as any[])[0]?.cancellationPolicy?.cancellationText
                )}
              </Paragraph>
            </Row>
            <Row>
              <Paragraph strong type='danger'>
                ***{t("hotel.extra_policy")}
              </Paragraph>
            </Row>
            <Row justify='end'>
              <Col flex={1}>
                <Button
                  block
                  size='large'
                  type='primary'
                  danger
                  onClick={() => refModalCancelBookingHotel.current?.open()}
                  disabled={!isCanCancel}
                  title={
                    !isCanCancel
                      ? t("hotel.booking_non_cancellable")
                      : t("hotel.cancel_booking")
                  }
                >
                  {!isCanCancel ? t("hotel.booking_non_cancellable") : t("hotel.cancel_booking")}
                </Button>
              </Col>
            </Row>
          </>
        )
      })}
    </>
  )
}

export default HotelBooking